import { HttpClient, HttpHeaders } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { Observable, from, of } from 'rxjs';
import { catchError, retry, switchMap } from 'rxjs/operators';

import { environment } from '../environments/environments';
import {
  base64ToBytes,
  createObfuscatedPayload,
  importAesKey,
} from './web-crypto.helper';

type ClientData = {
  fingerprint: string;
  timestamp: number;
  timezone: string;
  nonce: string;
};

@Injectable({ providedIn: 'root' })
export class BancardTokenService {
  readonly #http = inject(HttpClient);
  readonly #router = inject(Router);

  readonly #keyB64 = environment.CRYPTO_AES_KEY;
  readonly #ivB64 = environment.CRYPTO_AES_IV;

  readonly #keyPromise = importAesKey(this.#keyB64);
  readonly #iv = base64ToBytes(this.#ivB64);

  secureLogin(): Observable<void> {
    const loginData: ClientData = {
      fingerprint: this.generateClientFingerprint(),
      timestamp: Date.now(),
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      nonce: this.generateNonce(),
    };

    return this.callRoot().pipe(
      switchMap(() => this.loginWithoutToken(loginData)),
    );
  }

  private callRoot(): Observable<void> {
    // GET na raiz para inicializar cookie
    return this.#http
      .get<void>(environment.apiBaseUrl, {
        withCredentials: true,
      })
      .pipe(
        retry({ count: 3, delay: 500 }),
        catchError((error) => {
          console.warn('Root call failed, continuing to login', error);
          return of(void 0);
        }),
      );
  }

  invalidate(): void {
    // Neste fluxo de cookie, não há token em memória para invalidar
    // O cookie é gerenciado pelo navegador/servidor
  }

  private loginWithoutToken(loginData: ClientData): Observable<void> {
    const currentPath = window.location.href.slice(
      window.location.origin.length,
    );
    if (currentPath !== 'auth-fallback')
      sessionStorage.setItem('redirectTo', currentPath);

    return from(
      createObfuscatedPayload(loginData, this.#keyPromise, this.#iv),
    ).pipe(
      switchMap((payload) => this.cookieRequest(payload)),
      catchError((error) => this.handleRequestError(error)),
    );
  }

  private cookieRequest(payload: string): Observable<void> {
    const url = `${environment.apiBaseUrl}${environment.loginPath}`;
    return this.#http
      .post<void>(url, null, {
        headers: new HttpHeaders({
          'X-Client-Data': payload,
        }),
        withCredentials: true,
      })
      .pipe(retry({ count: 3, delay: 500 }));
  }

  private handleRequestError(error: any): Observable<void> {
    console.error('Authentication failed', error);
    this.#router.navigateByUrl('auth-fallback');
    return of(void 0);
  }

  private generateClientFingerprint(): string {
    const components = [
      navigator.userAgent,
      navigator.language,
      `${screen.width}x${screen.height}`,
      new Date().getTimezoneOffset().toString(),
      navigator.platform,
    ];

    let hash = 0;
    const combined = components.join('|');
    for (let i = 0; i < combined.length; i++) {
      const char = combined.charCodeAt(i);
      hash = (hash << 5) - hash + char;
      hash = hash & hash;
    }
    return Math.abs(hash).toString(36);
  }

  private generateNonce(): string {
    const bytes = new Uint8Array(16);
    crypto.getRandomValues(bytes);
    // hex string
    return Array.from(bytes)
      .map((b) => b.toString(16).padStart(2, '0'))
      .join('');
  }
}
